import { ASSIGNMENT_TYPE, RESERVATION_LABEL, SEGMENT_TYPE } from '@/types/capacityCalendar';
import type {
  AllocationSegment,
  AllocationSummary,
  CalendarEntry,
  CalendarRow,
  CapacityAllocationMap,
  DayAllocationPlacement,
  PoAllocation,
  WeekCapacityAllocation,
} from '@/types/capacityCalendar';
import type { CalendarBucket } from '@/utils/calendarBuckets';
import { isDecrease } from '@/utils/importPreview';
import type { PendingCell } from '@/utils/importPreview';

const DAYS_PER_WEEK = 7;
const FULL_PERCENT = 100;

// A reservation may carry a poNumber as a tracking reference and/or a comment;
// prefer the comment, fall back to the referenced PO, then to a generic label.
export function assignmentLabel(po: PoAllocation): string {
  if (po.type !== ASSIGNMENT_TYPE.manual) return po.poNumber ?? '';
  return po.notes?.trim() || po.poNumber || RESERVATION_LABEL;
}

export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateFromKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function groupCalendarRows(entries: CalendarEntry[]): CalendarRow[] {
  const groups = entries.reduce<Map<string, CalendarEntry[]>>((result, entry) => {
    const existing = result.get(entry.vendorCode) ?? [];
    existing.push(entry);
    result.set(entry.vendorCode, existing);
    return result;
  }, new Map());

  return Array.from(groups.values()).flatMap((vendorEntries) =>
    vendorEntries.map((entry, index) => ({
      ...entry,
      isFirstInVendorGroup: index === 0,
      vendorRowSpan: vendorEntries.length,
    }))
  );
}

// Capacity-type codes a distribution group's portions actually landed on
// for one vendor/week, resolved from the calendar's own rows/allocations
// rather than the PO's parsed data — so the wording always matches what the
// calendar shows. Shared by the full-page assignment view and the drawer so
// both report the same specific "distributed across X, Y" removal message.
export function resolveDistributionTypeCodes(params: {
  rows: CalendarRow[];
  allocations: CapacityAllocationMap;
  vendorId: string;
  weekKey: string;
  distributionGroupId: string;
}): string[] {
  const { rows, allocations, vendorId, weekKey, distributionGroupId } = params;
  return rows
    .filter((row) => row.vendorId === vendorId)
    .filter((row) => allocations[row.id]?.[weekKey]?.purchaseOrders.some((po) => po.distributionGroupId === distributionGroupId))
    .map((row) => row.typeCode);
}

export function buildDayAllocationPlacements(visibleDays: CalendarBucket[], weekKeys: string[]): Map<string, DayAllocationPlacement> {
  return weekKeys.reduce<Map<string, DayAllocationPlacement>>((placements, weekKey) => {
    const weekStart = dateFromKey(weekKey);
    const weekEnd = addDays(weekStart, DAYS_PER_WEEK - 1);
    const visibleWeekDays = visibleDays.filter((bucket) => bucket.startDate >= weekStart && bucket.startDate <= weekEnd);
    const firstVisibleDay = visibleWeekDays[0];

    if (firstVisibleDay) {
      placements.set(firstVisibleDay.key, {
        weekKey,
        visibleDaySpan: visibleWeekDays.length,
      });
    }

    return placements;
  }, new Map());
}


// Rolls the weeks whose weekStart falls inside [startDate, endDate] into one
// synthetic allocation (for month cells). A week belongs to the month it
// starts in; POs split across weeks merge into a single segment per number.
export function aggregateAllocationsInRange(weekAllocations: Record<string, WeekCapacityAllocation>, startDate: Date, endDate: Date): WeekCapacityAllocation | null {
  const weeks = Object.values(weekAllocations).filter((allocation) => {
    const weekStart = dateFromKey(allocation.weekKey);
    return weekStart >= startDate && weekStart <= endDate;
  });
  if (weeks.length === 0) {
    return null;
  }

  // Real POs merge/sum across weeks by poNumber (unchanged). Manual blocks have no
  // poNumber to merge on and shouldn't be summed together even if two land in the
  // same month — each is keyed by its own id so it survives the rollup as its own entry.
  const mergedByKey = new Map<string, PoAllocation>();
  weeks
    .flatMap((allocation) => allocation.purchaseOrders)
    .filter((po) => po.type === ASSIGNMENT_TYPE.manual || po.poNumber)
    .forEach((po) => {
      const key = po.type === ASSIGNMENT_TYPE.manual ? `${ASSIGNMENT_TYPE.manual}:${po.id}` : (po.poNumber as string);
      const existing = mergedByKey.get(key);
      if (existing) {
        mergedByKey.set(key, { ...existing, quantity: existing.quantity + po.quantity });
        return;
      }
      mergedByKey.set(key, { ...po });
    });

  const remainedCapacity = weeks.reduce((total, allocation) => total + allocation.remainedCapacity, 0);
  const planningCapacity = weeks.reduce((total, allocation) => {
    const poTotal = allocation.purchaseOrders.reduce((sum, po) => sum + po.quantity, 0);
    return total + (allocation.planningCapacity ?? poTotal + Math.max(allocation.remainedCapacity, 0));
  }, 0);

  const rangeKey = toDateKey(startDate);
  return {
    id: `range:${rangeKey}`,
    weekKey: rangeKey,
    purchaseOrders: [...mergedByKey.values()],
    remainedCapacity,
    planningCapacity,
  };
}

// Rolls whichever pending-import cells fall inside [startDate, endDate] (a month bucket) into
// one synthetic PendingCell for the rollup overlay — same "sum the weeks" approximation
// aggregateAllocationsInRange already uses for real allocations. `weekKeysInRange` is every
// week-bucket key overlapping the month; most resolve to no pending cell for this row.
export function aggregatePendingInRange(pendingCells: Map<string, PendingCell>, rowId: string, weekKeysInRange: string[]): PendingCell | null {
  const matches = weekKeysInRange.map((weekKey) => pendingCells.get(`${rowId}:${weekKey}`)).filter((cell): cell is PendingCell => Boolean(cell));
  if (matches.length === 0) {
    return null;
  }

  const [first] = matches;
  const kind: PendingCell['kind'] = matches.some((cell) => cell.kind === 'changed') ? 'changed' : 'new';
  const currentCapacity = matches.some((cell) => cell.currentCapacity !== null) ? matches.reduce((sum, cell) => sum + (cell.currentCapacity ?? 0), 0) : null;
  const nextCapacity = matches.reduce((sum, cell) => sum + cell.nextCapacity, 0);

  return {
    rowId,
    vendorId: first.vendorId,
    capacityTypeId: first.capacityTypeId,
    weekStart: first.weekStart,
    kind,
    currentCapacity,
    nextCapacity,
    // Summed totals can net out a cut against a bigger raise elsewhere in the month, which would
    // drop the DECREASE badge while the bar still counts the week. Carry the per-week verdict.
    containsDecrease: matches.some(isDecrease),
  };
}

// Whether any week-bucket key inside a month rollup carries a matched-import marker for this
// row — mirrors aggregatePendingInRange's "does this rollup touch any of these weeks" shape, but
// the marker itself has no values to sum (see MatchedCell): true is enough for the ring/badge.
export function aggregateMatchedInRange(matchedCells: Map<string, true>, rowId: string, weekKeysInRange: string[]): boolean {
  return weekKeysInRange.some((weekKey) => matchedCells.has(`${rowId}:${weekKey}`));
}

// Rows arrive in whatever order the backend wrote them, which differs per capacity type, so
// the same block lands at a different position in each bar. Callers that know the intended
// order (the scheduling board's block order) pass it; anything not listed keeps its place at
// the end, so an unexpected assignment is never dropped or hoisted.
function orderByKeys(purchaseOrders: PoAllocation[], orderKeys?: string[]): PoAllocation[] {
  if (!orderKeys || orderKeys.length === 0) {
    return purchaseOrders;
  }
  const rankByKey = new Map(orderKeys.map((key, index) => [key, index]));
  return [...purchaseOrders].sort(
    (first, second) =>
      (rankByKey.get(assignmentOrderKey(first)) ?? Number.MAX_SAFE_INTEGER) - (rankByKey.get(assignmentOrderKey(second)) ?? Number.MAX_SAFE_INTEGER),
  );
}

// Identifies an assignment the same way in every capacity type row it touches: a PO by its
// number, a manual block by its distribution group (or its own id when it stands alone).
// Ordering bars by this is what lets one block line up down the column.
function assignmentOrderKey(po: PoAllocation): string {
  if (po.type === ASSIGNMENT_TYPE.manual) {
    return po.distributionGroupId ?? (po.id as string);
  }
  return po.poNumber ?? (po.id as string);
}

// assignmentOrderKey's counterpart once an assignment has become a segment: the identity a
// block is hovered and selected by, everywhere it is drawn. Null for the remains segment,
// which is capacity rather than a block.
export function segmentBlockKey(segment: AllocationSegment): string | null {
  if (segment.type === SEGMENT_TYPE.manual) {
    return segment.distributionGroupId ?? segment.key;
  }
  return segment.type === SEGMENT_TYPE.po ? segment.key : null;
}

// Matches a segment against a block key from anywhere — another capacity row, a Scheduled-row
// card, a deep link. Both sides are checked because a distributed block is keyed by its group
// while each of its segments still carries its own key.
export function segmentMatchesBlockKey(segment: AllocationSegment, blockKey: string | null | undefined): boolean {
  if (!blockKey || segment.type === SEGMENT_TYPE.remains) return false;
  return segment.key === blockKey || segment.distributionGroupId === blockKey;
}

// The order a vendor's blocks take in one period, keyed `${vendorCode}|${bucketKey}`: first
// appearance walking that vendor's rows in row order. Every bar in the same column then ranks
// its segments the same way, so a block spread across capacity types reads as one vertical
// band instead of moving from row to row. The scheduling board takes this order from the
// Scheduled row's cards; the calendar has no such row, so its rows are the order.
//
// Only week keys are present: a month cell renders a synthesized rollup whose key is not one
// of these, and falls through to unordered — it is a read-only summary.
export function buildBlockOrder(rows: CalendarRow[], allocations: CapacityAllocationMap): Map<string, string[]> {
  const order = new Map<string, string[]>();
  rows.forEach((row) => {
    Object.entries(allocations[row.id] ?? {}).forEach(([bucketKey, allocation]) => {
      const cellKey = `${row.vendorCode}|${bucketKey}`;
      const keys = order.get(cellKey) ?? [];
      allocation.purchaseOrders.forEach((po) => {
        const blockKey = assignmentOrderKey(po);
        if (!keys.includes(blockKey)) {
          keys.push(blockKey);
        }
      });
      order.set(cellKey, keys);
    });
  });
  return order;
}

export function buildAllocationSegments(purchaseOrders: PoAllocation[], remainedCapacity: number, orderKeys?: string[]): AllocationSummary {
  const nonCapacityPos = purchaseOrders.filter((po) => po.quantity === 0);
  const countedPos = orderByKeys(
    purchaseOrders.filter((po) => po.quantity !== 0),
    orderKeys,
  );
  const poTotal = countedPos.reduce((sum, po) => sum + Math.max(po.quantity, 0), 0);
  const availableRemaining = Math.max(remainedCapacity, 0);
  const representedTotal = poTotal + availableRemaining;
  // Segments are sized against what the bar draws, so an overbooked bar fills to exactly
  // 100%. The utilization figure is against the planned capacity instead, so it keeps
  // growing past 100%. Zero planned capacity with assignments has no finite ratio.
  const plannedCapacity = poTotal + remainedCapacity;
  const utilizationPercent = plannedCapacity > 0 ? (poTotal / plannedCapacity) * FULL_PERCENT : poTotal > 0 ? FULL_PERCENT : 0;
  const status = remainedCapacity < 0 ? 'overbooked' : remainedCapacity === 0 ? 'full' : 'available';

  const poSegments = countedPos.map((po) => {
    if (po.type === ASSIGNMENT_TYPE.manual) {
      return {
        key: po.id as string,
        type: SEGMENT_TYPE.manual,
        label: assignmentLabel(po),
        quantity: po.quantity,
        percent: representedTotal > 0 ? (Math.max(po.quantity, 0) / representedTotal) * FULL_PERCENT : 0,
        // A multi-type manual block's rows share a group id — kept here so selecting the
        // block highlights every sibling segment. Group drag/tooltips stay po-only.
        distributionGroupId: po.distributionGroupId ?? null,
      };
    }
    return {
      key: po.poNumber as string,
      type: SEGMENT_TYPE.po,
      label: po.poNumber as string,
      quantity: po.quantity,
      percent: representedTotal > 0 ? (Math.max(po.quantity, 0) / representedTotal) * FULL_PERCENT : 0,
      distributionGroupId: po.distributionGroupId ?? null,
    };
  });

  const remainsSegment =
    availableRemaining > 0
      ? [
          {
            key: SEGMENT_TYPE.remains,
            type: SEGMENT_TYPE.remains,
            label: 'Remains',
            quantity: availableRemaining,
            percent: representedTotal > 0 ? (availableRemaining / representedTotal) * FULL_PERCENT : 0,
          },
        ]
      : [];

  return {
    status,
    utilizationPercent,
    overbookedBy: Math.max(-remainedCapacity, 0),
    segments: [...poSegments, ...remainsSegment],
    nonCapacityPos,
  };
}

// Molding rows carry a unit (e.g. "parts") so a bare count like "12,400" isn't
// ambiguous with a termination row's count; termination rows have unit === null
// and stay bare. `suffix` is appended after the unit, e.g. formatCapacityQuantity(12400, 'parts', 'left') -> "12,400 parts left".
export function formatCapacityQuantity(value: number, unit: string | null, suffix?: string): string {
  const formatted = value.toLocaleString();
  const withUnit = unit ? `${formatted} ${unit}` : formatted;
  return suffix ? `${withUnit} ${suffix}` : withUnit;
}

// Mirrors the backend's import-time formula (capacity-allocations.service.ts's buildDrafts:
// planning = round(capacity * factor)) so editing the factor in the allocation drawer
// recomputes planning capacity the same way a fresh import would have written it.
// Null when capacity is unknown — the caller must leave planningCapacity untouched then.
export function computePlanningCapacity(capacity: number | null, capacityFactor: number): number | null {
  if (capacity === null || !Number.isFinite(capacityFactor)) return null;
  return Math.round(capacity * capacityFactor);
}
